import { useState } from "react";
import AiService from "services/ai-service";

/**
 * Hook to handle Chart Analysis API calls and data optimization.
 * @param {Object} chartData - The main chart dataset.
 * @param {string} title - Chart title.
 * @param {string} context - Analysis context.
 * @param {Object} rawData - Raw data for optimization.
 * @param {Object} entitlements - User entitlements for permission check.
 * @param {Function} updateUsage - Function to update usage state.
 * @returns {Object} { analyze, loading, error, response, resetState, showUpgrade, setShowUpgrade }
 */
export const useChartAnalysis = ({ chartData, title, context, rawData, entitlements, updateUsage }) => {
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState(null);
    const [error, setError] = useState(null);
    const [showUpgrade, setShowUpgrade] = useState(false);

    // Optimize rawData to reduce payload size (cost control)
    const optimizeRawData = (data) => {
        if (!data) return [];
        // Deep clone to avoid mutating state
        const optimized = JSON.parse(JSON.stringify(data));

        optimized.forEach((profile) => {
            if (profile.data) {
                profile.data.forEach((day) => {
                    // Optimize POSTS
                    if (day.posts && Array.isArray(day.posts)) {
                        day.posts.forEach((post) => {
                            // Cap comments to top 20 (usually enough for sentiment)
                            if (post.comments && post.comments.length > 20) {
                                // Keep only relevant fields for AI context
                                post.comments = post.comments.slice(0, 20).map(c => ({
                                    content: c.content,
                                    sentiment: c.sentiment?.label,
                                    likes: c.likes
                                }));
                            } else if (post.comments) {
                                // Minimal fields even for small lists
                                post.comments = post.comments.map(c => ({
                                    content: c.content,
                                    sentiment: c.sentiment?.label,
                                    likes: c.likes
                                }));
                            }

                            // Remove heavy fields from post if not needed for chart analysis
                            delete post.full_picture;
                            delete post.permalink_url;
                            delete post.attachment_target_id;
                        });
                    }

                    // Optimize VIDEOS (YouTube) similarly if needed
                    if (day.videos && Array.isArray(day.videos)) {
                        day.videos.forEach(video => {
                            delete video.thumbnails;
                        });
                    }
                });
            }
        });
        return optimized;
    };

    const analyze = async (query) => {
        // 🔒 ENTITLEMENT CHECK
        if (entitlements) {
            const isEntitled = entitlements.features?.includes("AI Powered Insight");
            if (!isEntitled) {
                setShowUpgrade(true);
                return;
            }
        }

        if (!query.trim()) return;

        setLoading(true);
        setError(null);
        setResponse(null);

        try {
            // Aggressive Sanitization: Force primitives to strip hidden DOM/React nodes
            const cleanDatasets = (chartData.datasets || []).map(ds => ({
                label: String(ds.label || "Dataset"), // Force String
                data: (ds.data || []).map(num => Number(num) || 0), // Force Number
                platform: String(ds.platform || "")
            }));

            // Force labels to be pure strings 
            const cleanLabels = (chartData.labels || []).map(l => String(l));

            const sanitizedPayload = {
                labels: cleanLabels,
                datasets: cleanDatasets,
                rawData: rawData ? optimizeRawData(rawData) : null
            };

            const result = await AiService.analyzeChart(
                String(context || title || "Chart Analysis"),
                sanitizedPayload,
                query
            );

            setResponse(result.answer);
            if (result.usage && updateUsage) {
                updateUsage(result.usage);
            }
        } catch (err) {
            console.error("AI Analysis Error:", err);
            const msg = err.response?.data?.message || err.message || "Something went wrong";
            setError(`Error: ${msg}`);

            // Handle 429 Rate Limit
            if (err.response?.status === 429) {
                const { limit, usage: currentUsage } = err.response.data;
                if (updateUsage) {
                    updateUsage({
                        current: currentUsage,
                        limit: limit,
                        remaining: Math.max(0, limit - currentUsage)
                    });
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const resetState = () => {
        setResponse(null);
        setError(null);
    };

    return { analyze, loading, error, response, resetState, showUpgrade, setShowUpgrade };
};
